import { dehydrate, QueryClient, useQuery } from '@tanstack/react-query';
import {
    Award,
    Body,
    Box,
    EditionNavigation,
    EditionNavItem,
    Flex,
    Grid,
    Heading,
    Hero,
    HtmlContent,
    LightTable,
    MediaModal,
    MediaSlider,
    Meta,
    Stack,
    Tag,
    TheaterModal,
    useTheaterModal,
    useTheme,
    Wrapper,
} from 'designsystem';
import { GetStaticPaths, GetStaticProps } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { FilmDetailDocument, FilmDetailQuery, FilmDetailQueryVariables, useFilmDetailQuery } from '../../../gql/api';
import {
    EditionType,
    fetchApiData,
    getTitleFromEditionType,
    gtm,
    LoadingFilmPage,
    slugifyTitle,
    useApi,
    useFilmInfo,
    VideoButtons,
} from 'shared';
import loadIntlMessages from '../../../utils/loadIntlMessages';
import prefetchGlobals from '../../../utils/prefetchGlobals';
import { NextSeo } from 'next-seo';
import fetchArchiveUri from '../../../utils/fetchArchiveUri';
import InstituteBreadcrumbs from '../../../components/InstituteBreadcrumbs';
import { useArchivePathQuery } from '../../../gql/cms';
import siteHandleByLocale from '../../../utils/siteHandleByLocale';
import striptags from 'striptags';
import React, { useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { allowedEditionTypes, editionMessages } from '../../../constants/relevantEditionTypes';
import { useSession } from 'next-auth/react';
import { DEFAULT_REVALIDATION_TIME } from '../../[...uri]';

const FilmArchiveDetail = () => {
    const { formatMessage } = useIntl();
    const { query, locale, asPath, isFallback } = useRouter();
    const { status } = useSession();
    const vars = {
        id: query.id as string,
        language: locale,
    };
    const { data, isLoading } = useQuery<FilmDetailQuery>(
        [...useFilmDetailQuery.getKey(vars), { loggedIn: status === 'authenticated' }],
        useApi<FilmDetailQuery, FilmDetailQueryVariables>(FilmDetailDocument).bind(null, vars),
        { enabled: !!query.id }
    );
    const theme = useTheme();

    const film = data?.film;

    const {
        title,
        translatedTitle,
        meta,
        heroImage,
        ogImage,
        description,
        editions,
        primaryAward,
        edition,
        films,
        trailers,
        credits,
        mediaVideos,
        mediaImages,
        mediaModalProps,
        subjects,
    } = useFilmInfo({ film: data?.film, allowedEditionTypes });

    const filmYear = meta.items.filter(m => m.key === 'year')[0]?.value;
    const { videoSrc, handleVideoClick, ...theaterDisclosureProps } = useTheaterModal();

    const archiveUri = useArchivePathQuery(
        {
            site: siteHandleByLocale[locale],
        },
        {
            enabled: !isFallback,
        }
    )?.data?.entry?.uri;

    useEffect(() => {
        if (film) {
            gtm.pageView(asPath, {
                page_pillar: 'institute',
                page_navigation: archiveUri,
                page_language: locale,
                film_name: film.fullPreferredTitle,
                film_id: film.id,
                film_edition: film.edition.year.toString(),
                film_section: film.sections?.join(', ') ?? '',
                film_subject:
                    film.subject?.map(s => s.translation).join(', ') ??
                    film.tags?.map(tag => tag.translation).join(', ') ??
                    '',
            });
        }
    }, [film, asPath, archiveUri, subjects, locale]);

    const filmTagTitle = useMemo(() => {
        if (!edition) {
            return null;
        }
        const editionTitle = editionMessages[edition.id] ? formatMessage(editionMessages[edition.id]) : edition.name;
        return getTitleFromEditionType(editionTitle as EditionType, true);
    }, [edition, formatMessage]);

    if (isFallback || isLoading) {
        return <LoadingFilmPage />;
    }

    return (
        <>
            {film && (
                <NextSeo
                    title={`${title} (${filmYear ?? edition.year})`}
                    description={striptags(description)}
                    // the most recent film is the 'canonical' film to prevent a film from getting duplicated in search results.
                    canonical={`${process.env.NEXT_PUBLIC_BASE_URL ?? ''}${locale === 'en' ? '/en' : ''}/film/${
                        editions[0].id
                    }/${slugifyTitle(title)}`}
                    openGraph={{
                        images: ogImage,
                    }}
                    titleTemplate="%s | IDFA Archive"
                    languageAlternates={[
                        {
                            hrefLang: 'en',
                            href: `${process.env.NEXT_PUBLIC_BASE_URL ?? ''}/en/film/${editions[0].id}/${slugifyTitle(
                                title
                            )}`,
                        },
                    ]}
                />
            )}
            <Hero
                image={heroImage}
                topChildren={
                    <InstituteBreadcrumbs
                        parentUri={archiveUri}
                        currentPage={{
                            title,
                            uri: asPath,
                        }}
                        onImage
                    />
                }
                // removed for now, add back when collection pillar is reintroduced
                // rightChildren={
                //     <CollectionTile
                //         gridRow="2"
                //         h="auto"
                //         minH="50%"
                //         title={
                //             <FormattedMessage
                //                 description="Collection tile: title"
                //                 defaultMessage="Bekijk nu in de IDFA Collection"
                //             />
                //         }
                //         description={
                //             <FormattedMessage
                //                 description="Collection tile: description"
                //                 defaultMessage="{filmTitle} is nu beschikbaar in de IDFA Collection"
                //                 values={{ filmTitle: film?.fullPreferredTitle }}
                //             />
                //         }
                //         href="/"
                //     />
                // }
            >
                {film && (
                    <Stack spacing={7}>
                        <Stack spacing={4}>
                            <Box>
                                {filmTagTitle && (
                                    <Tag color={theme.colors.ColorNeutralWhite}>
                                        {getTitleFromEditionType(filmTagTitle as EditionType, true)}
                                    </Tag>
                                )}
                            </Box>
                            <Stack mt={2}>
                                <Heading as="h1" variant={1} clamp={3}>
                                    {title}
                                </Heading>
                                {translatedTitle && <Heading variant={5}>{translatedTitle}</Heading>}
                            </Stack>
                            <Box>
                                <Meta {...meta} size="m" />
                            </Box>
                        </Stack>
                        <VideoButtons
                            films={films}
                            trailers={trailers}
                            handleVideoClick={handleVideoClick}
                            filmExternal
                        />
                    </Stack>
                )}
            </Hero>
            <TheaterModal title={title} meta={{ ...meta }} videoSrc={videoSrc} {...theaterDisclosureProps} />
            {film && (
                <Flex flexDir="column" gap={[9, null, 11]}>
                    <EditionNavigation
                        translatedTitle={<FormattedMessage defaultMessage="Festival history" />}
                        order={[1, null, 0]}
                        editions={editions.map(e => (
                            <Link href={`/film/${e.id}/${query.slug}`} key={e.id}>
                                <EditionNavItem variant={e.selected ? 'solid' : 'outline'}>
                                    {getTitleFromEditionType(e.name as EditionType, true)}
                                </EditionNavItem>
                            </Link>
                        ))}
                    />
                    <Wrapper order={[0, null, 1]} alignItems="center">
                        <Grid>
                            {description && (
                                <Box gridColumn={['1 / -1', null, '1 / span 9', '1 / span 8', '1 / span 7']}>
                                    <Stack spacing={6}>
                                        {primaryAward && (
                                            <Stack spacing={3}>
                                                <Award mb={3}>{primaryAward.translation}</Award>
                                            </Stack>
                                        )}
                                        <Body>
                                            <Stack spacing={4}>
                                                <HtmlContent html={description} />
                                            </Stack>
                                        </Body>
                                    </Stack>
                                </Box>
                            )}
                        </Grid>
                    </Wrapper>
                    <Wrapper order={2}>
                        <Grid>
                            {credits?.length > 0 && (
                                <Box gridColumn={['1 / -1', null, '1 / span 9', '1 / span 8']}>
                                    <Stack spacing={6}>
                                        <Heading variant={3}>Credits</Heading>
                                        <LightTable lines={credits} />
                                    </Stack>
                                </Box>
                            )}
                        </Grid>
                    </Wrapper>
                    {mediaVideos.length > 0 && (
                        <Stack as="section" spacing={6} order={3}>
                            <Wrapper>
                                <Heading variant={3} as="h2">
                                    <FormattedMessage defaultMessage="Video's" />
                                </Heading>
                            </Wrapper>
                            <MediaSlider media={mediaVideos.filter(Boolean)} />
                        </Stack>
                    )}
                    {mediaImages.length > 0 && (
                        <Stack as="section" spacing={6} order={4}>
                            <Wrapper>
                                <Heading variant={3} as="h2">
                                    <FormattedMessage defaultMessage="Images" />
                                </Heading>
                            </Wrapper>
                            <MediaSlider media={mediaImages.filter(Boolean)} />
                        </Stack>
                    )}
                    <MediaModal
                        {...mediaModalProps}
                        closeLabel={<FormattedMessage defaultMessage="Sluiten" description="Close modal" />}
                    />
                </Flex>
            )}
        </>
    );
};

export const getStaticPaths: GetStaticPaths = async () => ({
    // we don't provide paths because we won't fetch any films during build time.
    paths: [],

    fallback: true,
});

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
    const queryClient = new QueryClient();

    const variables = {
        id: params?.id as string,
        language: locale,
    };
    const data = await queryClient.fetchQuery([...useFilmDetailQuery.getKey(variables), { loggedIn: false }], () =>
        fetchApiData<FilmDetailQuery, FilmDetailQueryVariables>({
            locale,
            query: useFilmDetailQuery.document,
            variables,
        })
    );
    if (!data.film || data.film?.isAlternativeContent) {
        return {
            notFound: true,
            revalidate: DEFAULT_REVALIDATION_TIME,
        };
    }

    const archiveUri = await fetchArchiveUri(queryClient, locale);
    await prefetchGlobals(queryClient, locale, { pathType: 'PARENT_URI', uri: archiveUri });

    return {
        props: {
            intlMessages: await loadIntlMessages(locale),
            dehydratedState: dehydrate(queryClient),
        },
        revalidate: DEFAULT_REVALIDATION_TIME,
    };
};

export default FilmArchiveDetail;
